import { FC, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useIdleTimer } from 'react-idle-timer'
import { useDispatch, useSelector } from 'react-redux'
import { Navigate, Route, Routes as RoutesWrapper, useLocation } from 'react-router-dom'
import { useLanguageContext } from '../contexts/LanguageContext'
// pages
import ForgotPasswordPage from '../pages/auth/ForgotPasswordPage'
import LoginPage from '../pages/auth/LoginPage'
import ResetPasswordPage from '../pages/auth/ResetPasswordPage'
import DownloadAppPage from '../pages/DownloadAppPage'
import NotFoundPage from '../pages/NotFoundPage'
import WelcomeFormPage from '../pages/welcomePage/WelcomeFormPage'
import WithdrawalsDashboard from '../pages/withdrawals/WithdrawalsDashboard'
import { RootState } from '../redux'
import { IUserAuthPayload, userAuthRefreshToken } from '../redux/userAuth/userAuthActions'
import { getRefreshToken, hasAccessToken, hasRefreshToken } from '../utils/auth'
import { REFRESH_PAGE_INTERVAL, REFRESH_TOKEN_INTERVAL } from '../utils/enums'
// routes
import AuthRoutes from './AuthRoutes'
import EmployeeRoutes from './EmployeeRoutes'
import PublicRoutes from './PublicRoutes'

const Routes: FC = () => {
	const { t } = useTranslation()
	const dispatch = useDispatch()
	const location = useLocation()
	const { currentLanguage, getLocalizedPath, isInitialized } = useLanguageContext()

	const userAuth: IUserAuthPayload = useSelector((state: RootState) => state.userAuth)

	// Helper function to convert absolute paths to relative paths for nested routes
	const getRelativePath = (absolutePath: string): string => {
		return absolutePath.startsWith('/') ? absolutePath.slice(1) : absolutePath
	}

	// Helper function to get localized path with preserved query parameters and hash
	const getLocalizedPathWithParams = (path: string): string => {
		const localizedPath = getLocalizedPath(path)
		return `${localizedPath}${location.search}${location.hash}`
	}

	useIdleTimer({
		element: document,
		timeout: REFRESH_PAGE_INTERVAL,
		debounce: 250,
		onIdle: () => document.location.reload()
	})

	useEffect(() => {
		const refreshToken = () => {
			if (hasAccessToken() && hasRefreshToken()) {
				dispatch(userAuthRefreshToken(getRefreshToken() as string))
			}
		}
		const refreshTokenInterval = setInterval(refreshToken, REFRESH_TOKEN_INTERVAL)
		return () => clearInterval(refreshTokenInterval)
	}, [dispatch])

	// Wait for language context to initialize before rendering routes
	if (!isInitialized) {
		return null
	}

	return (
		<RoutesWrapper>
			{/* Language-prefixed routes */}
			<Route path={`/${currentLanguage}/*`}>
				{/* Index route for language prefix - redirect to login */}
				<Route index element={<Navigate to={getRelativePath(t('paths:login'))} replace />} />

				<Route element={<PublicRoutes hideTitle />}>
					<Route path={getRelativePath(t('paths:welcome-page'))} element={<WelcomeFormPage />} />
					<Route path={getRelativePath(t('paths:download-app'))} element={<DownloadAppPage />} />
				</Route>
				<Route element={<AuthRoutes />}>
					<Route path={getRelativePath(t('paths:login'))} element={<LoginPage />} />
					<Route path={getRelativePath(t('paths:forgot-password'))} element={<ForgotPasswordPage />} />
					<Route path={getRelativePath(t('paths:reset-password'))} element={<ResetPasswordPage />} />
				</Route>
				{userAuth.user && (
					<Route element={<EmployeeRoutes />}>
						<Route path={getRelativePath(t('paths:withdrawals'))} element={<WithdrawalsDashboard />} />
						<Route path={'*'} element={<NotFoundPage />} />
					</Route>
				)}
			</Route>

			{/* Redirect root path to language-prefixed login page */}
			<Route path='/' element={<Navigate to={getLocalizedPathWithParams(t('paths:login'))} replace />} />

			{/* Backward compatibility: redirect paths without language prefix */}
			<Route path={t('paths:welcome-page')} element={<Navigate to={getLocalizedPathWithParams(t('paths:welcome-page'))} replace />} />
			<Route path={t('paths:download-app')} element={<Navigate to={getLocalizedPathWithParams(t('paths:download-app'))} replace />} />
			<Route path={t('paths:login')} element={<Navigate to={getLocalizedPathWithParams(t('paths:login'))} replace />} />
			<Route path={t('paths:forgot-password')} element={<Navigate to={getLocalizedPathWithParams(t('paths:forgot-password'))} replace />} />
			<Route path={t('paths:reset-password')} element={<Navigate to={getLocalizedPathWithParams(t('paths:reset-password'))} replace />} />
			<Route path={t('paths:withdrawals')} element={<Navigate to={getLocalizedPathWithParams(t('paths:withdrawals'))} replace />} />

			{/* Catch-all redirect for unknown paths */}
			<Route path='*' element={<Navigate to={getLocalizedPathWithParams(t('paths:login'))} replace />} />
		</RoutesWrapper>
	)
}

export default Routes
